import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { MarketingAttributionEvent } from "@redotech/redo-model/marketing";
import { RedoMixedBarAndLineChart } from "@redotech/redo-web/arbiter-components/charts/line-and-bar-chart/redo-mixed-bar-and-line-chart";
import {
  DataPoint,
  MixedChartSeries,
} from "@redotech/redo-web/arbiter-components/charts/line-and-bar-chart/types";
import { RedoMetricItem } from "@redotech/redo-web/arbiter-components/metric-item/redo-metric-item";
import { Card } from "@redotech/redo-web/card";
import { CurrencyContext } from "@redotech/redo-web/currency";
import { Flex } from "@redotech/redo-web/flex";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useMemo } from "react";
import {
  ChannelFilter,
  getChannelsForFilter,
} from "../filters/marketing-channel-filter";

export const MarketingAnalyticsKeyRevenueMetrics = memo(
  function MarketingAnalyticsKeyRevenueMetrics({
    dateFilter,
    attributionDays,
    channelFilter,
    attributionEvents,
  }: {
    dateFilter: [Temporal.PlainDate, Temporal.PlainDate] | undefined;
    attributionDays: number;
    channelFilter: ChannelFilter;
    attributionEvents: MarketingAttributionEvent[];
  }) {
    const { formatCurrency } = useContext(CurrencyContext);

    const { startDate, endDate } = useMemo(() => {
      if (!dateFilter) {
        // Default to last 30 days if no valid date range
        const now = new Date();
        const thirtyDaysAgo = new Date(
          now.getTime() - 30 * 24 * 60 * 60 * 1000,
        );
        return {
          startDate: Temporal.Instant.from(thirtyDaysAgo.toISOString()),
          endDate: Temporal.Instant.from(now.toISOString()),
        };
      }
      return {
        startDate: Temporal.Instant.from(
          new Date(dateFilter[0].toString()).toISOString(),
        ),
        endDate: Temporal.Instant.from(
          new Date(dateFilter[1].toString()).toISOString(),
        ),
      };
    }, [dateFilter]);

    const {
      data: dailyMetricsResult,
      isLoading,
      error: failedToLoad,
    } = useRpcQuery(RedoMarketingRpcClientContext, {
      method: "getDailyRevenueMetrics",
      args: {
        startDate,
        endDate,
        attributionDays,
        channels: getChannelsForFilter(channelFilter),
        attributionEvents,
      },
    });

    const calculatePercentageChange = (current: number, previous: number) => {
      if (previous === 0 && current === 0) return 0;
      if (previous === 0) {
        return null;
      }

      const change = ((current - previous) / previous) * 100;

      // Cap extremely large percentage changes for display purposes
      if (change > 999) return 999;
      if (change < -999) return -999;

      return change;
    };

    const totals = dailyMetricsResult?.totals;

    const revenueChange = totals
      ? calculatePercentageChange(
          totals.totalRevenue,
          totals.previousTotalRevenue,
        )
      : 0;
    const spendChange = totals
      ? calculatePercentageChange(totals.totalSpend, totals.previousTotalSpend)
      : 0;
    const roiChange = totals
      ? calculatePercentageChange(totals.totalROI, totals.previousTotalROI)
      : 0;

    // Prepare chart data
    const chartSeries = useMemo((): MixedChartSeries[] => {
      if (!dailyMetricsResult?.dailyMetrics) {
        return [];
      }

      const revenueData: DataPoint[] = dailyMetricsResult.dailyMetrics.map(
        (metric) => ({
          date: Temporal.Instant.from(new Date(metric.DATE).toISOString()),
          value: metric.TOTAL_REVENUE,
        }),
      );

      const spendData: DataPoint[] = dailyMetricsResult.dailyMetrics.map(
        (metric) => ({
          date: Temporal.Instant.from(new Date(metric.DATE).toISOString()),
          value: metric.TOTAL_SPEND,
        }),
      );

      return [
        {
          name: "Revenue",
          data: revenueData,
          color: "#FF4405",
          type: "line" as const,
        },
        {
          name: "Spend",
          data: spendData,
          color: "#424242",
          type: "bar" as const,
        },
      ];
    }, [dailyMetricsResult?.dailyMetrics]);

    const valueFormatter = (value: number) => {
      // Format value as currency but without cents for cleaner display
      return formatCurrency(value).split(".")[0];
    };

    const formatPercentage = (value: number) => {
      return (value / 100).toLocaleString(undefined, {
        maximumFractionDigits: 1,
        minimumFractionDigits: 1,
        style: "percent",
      });
    };

    if (failedToLoad) {
      return (
        <Flex dir="column" gap="4xl" w="full">
          <Text fontSize="md" fontWeight="semibold">
            Key revenue metrics
          </Text>
          <Text color="red.500">Error loading revenue metrics data</Text>
        </Flex>
      );
    }

    return (
      <Flex dir="column" gap="xl" w="full">
        <Text fontSize="md" fontWeight="semibold">
          Key revenue metrics
        </Text>

        {/* Metric Cards */}
        <Flex dir="row" gap="4xl" w="full">
          <RedoMetricItem
            pending={isLoading}
            title="Revenue"
            value={formatCurrency(totals?.totalRevenue ?? 0)}
            {...(revenueChange !== null && {
              comparisonChange: revenueChange,
              comparisonText: "vs previous period",
              isGoodChange: revenueChange >= 0,
            })}
          />
          <RedoMetricItem
            pending={isLoading}
            title="Spend"
            value={formatCurrency(totals?.totalSpend ?? 0)}
            {...(spendChange !== null && {
              comparisonChange: spendChange,
              comparisonText: "vs previous period",
              isGoodChange: spendChange <= 0,
            })}
          />
          <RedoMetricItem
            pending={isLoading}
            title="ROI"
            value={formatPercentage(totals?.totalROI ?? 0)}
            {...(roiChange !== null && {
              comparisonChange: roiChange,
              comparisonText: "vs previous period",
              isGoodChange: roiChange >= 0,
            })}
          />
        </Flex>

        <Text fontSize="md" fontWeight="semibold">
          Performance
        </Text>

        {/* Chart */}
        <Card gap="xl" p="xl" w="full">
          <Text fontSize="sm" fontWeight="medium" textColor="secondary">
            Revenue and spend
          </Text>
          {isLoading ? (
            <Flex align="center" justify="center" py="8xl">
              <LoadingRedoAnimation />
            </Flex>
          ) : (
            <RedoMixedBarAndLineChart
              legendPosition="top"
              series={chartSeries}
              showTooltipSeriesNames
              valueAxisFormatter={valueFormatter}
              valueTooltipFormatter={(value: number) => formatCurrency(value)}
            />
          )}
        </Card>
      </Flex>
    );
  },
);
